/**
 * 檔案用途區塊
 * @module third-party-provider.model
 * @purpose 定義第三方登入參數型別與 Zustand 狀態容器，集中管理 provider/clientId/redirectUrl/lang 初始化資料。
 * @external https://innotech.atlassian.net/browse/IN-46601
 * @external https://innotech.atlassian.net/browse/IN-113493
 */
import { LocaleDash, PasskeyAction } from '@shared/enums';
import { ThirdPartyProvider } from '@shared/enums/third-party-provider.enum';
import { create } from 'zustand';

export type CommonThirdPartyPayload = {
  clientId: Nullable<string>;
  redirectUrl: Nullable<string>;
  lang: LocaleDash;
};

export type StandardThirdPartyProvider = Exclude<ThirdPartyProvider, ThirdPartyProvider.Passkey>;

export type StandardThirdPartyPayload = CommonThirdPartyPayload & {
  provider: Nullable<StandardThirdPartyProvider>;
};

export type PasskeyThirdPartyPayload = CommonThirdPartyPayload & {
  provider: ThirdPartyProvider.Passkey;
  action: PasskeyAction;
  state?: string;
  ceremonyId?: string;
  optionsJson?: string;
  optionsHandoffKey?: string;
  account?: string;
};

export type ThirdPartyPayload = StandardThirdPartyPayload | PasskeyThirdPartyPayload;

interface IThirdPartyProvider {
  payload: ThirdPartyPayload;
  init: ArrowFn<ThirdPartyPayload>;
}

/**
 * 宣告內容用途說明與單號關聯
 * @description 建立第三方登入資料的全域 store，提供 payload 狀態與 init 初始化方法。
 * @purpose 讓頁面可在載入時一次寫入 provider、clientId、redirectUrl 與語系設定。
 * @external https://innotech.atlassian.net/browse/IN-46601
 */
export const useThirdPartyProvider = create<IThirdPartyProvider>()((set) => ({
  payload: {
    provider: undefined,
    clientId: '',
    redirectUrl: '',
    lang: LocaleDash.en_US,
  },
  init: (payload: ThirdPartyPayload) => {
    /**
     * 宣告內容用途說明與單號關聯
     * @description 將解析後的第三方或 Passkey payload 整包寫入 store。
     * @purpose 保留 Passkey 專用欄位（action、handoff 等）與標準 OAuth 欄位，供下游 Guard 與 ceremony 使用。
     * @external https://innotech.atlassian.net/browse/IN-132161
     */
    set({
      payload,
    });
  },
}));

/**
 * === llm 分析紀錄區 ===
 * @llm-review-submitted-at 2026-06-14T07:22:48.378Z
 * @llm-review-model gpt-5.3-codex
 * @llm-review-note Added required three-section annotation layout (top/middle/bottom), inserted declaration-level comments for mapped signatures using only provided tickets, formatted all @external entries as full Jira browse URLs, and kept runtime logic unchanged.
 */
